import { useAllSubscriptions } from '@hooks/api/subscription';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Button } from '@components/atoms/Button';
import { NoAccounts } from '@mui/icons-material';
import formatDate from '@utils/formatDate';

interface AdminBillingListProps {
  page: number;
  search: string;
  filter: string;
  orderBy: string | null;
  rowsPerPage: number;
  orderDirection: 'asc' | 'desc';
  handleChangePage: (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  handleRequestSort: (property: string) => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilterChange: (filter: string) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const AdminBillingList: React.FC<AdminBillingListProps> = ({
  page,
  search,
  filter,
  orderBy,
  rowsPerPage,
  orderDirection,
  handleChangePage,
  handleRequestSort,
  handleSearchChange,
  handleFilterChange,
  handleChangeRowsPerPage,
}) => {
  // Fetch all subscriptions (Super Admin only)
  const subscriptionsQuery = useAllSubscriptions({
    page,
    limit: rowsPerPage,
    search,
    filter: filter === 'ALL' ? undefined : filter,
    orderBy: orderBy || 'nextBillingDate',
    orderDirection,
  });

  const activeFilterItems: SelectOption[] = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'TRIAL', label: 'Trial' },
    { value: 'PAST_DUE', label: 'Past Due' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'CANCELED', label: 'Canceled' },
    { value: 'ALL', label: 'All' },
  ];

  const statusColorMap: Record<string, string> = {
    trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    past_due: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const sortedTableHeaderCell = ({
    label,
    property,
    align,
  }: {
    label: string;
    property: string;
    align?: 'right' | 'left' | 'center' | 'inherit' | 'justify' | undefined;
  }) => {
    const alignClass =
      align === 'center'
        ? 'text-center'
        : align === 'right'
          ? 'text-right'
          : 'text-left';

    const sortIcon =
      orderBy === property ? (orderDirection === 'asc' ? ' ↑' : ' ↓') : '';

    return (
      <th
        className={`px-6 py-3 ${alignClass} text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`}
        onClick={() => handleRequestSort(property)}>
        {label}
        {sortIcon}
      </th>
    );
  };

  const subscriptions = subscriptionsQuery.data?.subscriptions || [];
  const count = subscriptionsQuery.data?.count || 0;

  return (
    <div className="w-full">
      <div className="flex items-center mb-4 gap-4">
        <div className="min-w-[250px]">
          <Input
            type="text"
            placeholder="Search by organization..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="min-w-[200px]">
          <ReactSelect
            value={
              activeFilterItems.find((item) => item.value === filter) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleFilterChange(selectedValue?.value?.toString() || 'ALL');
            }}
            options={activeFilterItems}
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>

      {subscriptionsQuery.isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 dark:border-purple-400"></div>
        </div>
      ) : count === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <NoAccounts className="text-gray-400 dark:text-gray-500 mb-4" sx={{ fontSize: 64 }} />
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {search
              ? 'No subscriptions found matching your search.'
              : filter === 'ALL'
                ? 'No subscriptions found.'
                : `No ${filter.toLowerCase()} subscriptions found.`}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {sortedTableHeaderCell({ label: 'Organization', property: 'organizationName' })}
                  {sortedTableHeaderCell({ label: 'Status', property: 'status', align: 'center' })}
                  {sortedTableHeaderCell({ label: 'Billing Cycle', property: 'billingCycle' })}
                  {sortedTableHeaderCell({ label: 'Amount', property: 'amount', align: 'right' })}
                  {sortedTableHeaderCell({ label: 'Next Billing', property: 'nextBillingDate' })}
                  {sortedTableHeaderCell({ label: 'Trial End', property: 'trialEndDate' })}
                  {sortedTableHeaderCell({ label: 'Failed Payments', property: 'failedPaymentAttempts', align: 'center' })}
                  {sortedTableHeaderCell({ label: 'Created By', property: 'createdByEmail' })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {subscriptions.map((subscription) => (
                  <tr
                    key={subscription.subscriptionId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {subscription.organizationName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColorMap[subscription.status] || statusColorMap.expired}`}>
                        {subscription.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {subscription.billingCycle === 'monthly' ? 'Monthly' : 'Annual'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right">
                      {formatCurrency(subscription.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {subscription.nextBillingDate
                        ? formatDate(subscription.nextBillingDate)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {subscription.trialEndDate
                        ? formatDate(subscription.trialEndDate)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${subscription.failedPaymentAttempts > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                        {String(subscription.failedPaymentAttempts)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {subscription.createdByEmail || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-end px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 mt-4 rounded-lg">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {count === 0
                  ? '0 of 0'
                  : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, count)} of ${count}`}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="tertiary"
                  size="md"
                  onClick={(e) => handleChangePage(e, page - 1)}
                  disabled={page === 0}>
                  Previous
                </Button>
                <Button
                  variant="tertiary"
                  size="md"
                  onClick={(e) => handleChangePage(e, page + 1)}
                  disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminBillingList;
